<template>
  <new-modal-dialog
    persistent
    scrollable
    max-width="700"
  >
    <div class="my-1 headline">
      {{ $t('title.whats_new') }}
    </div>

    <div
      v-for="(release, index) in visibleReleaseNotes"
      :key="index"
      class="mb-7"
    >
      <div class="mt-4 mb-1">
        <h3 class="text-subtitle-1 mb-1 font-weight-medium">
          {{ $localized.dateText(release.date) }}
        </h3>
        <v-divider />
      </div>

      <div
        v-if="release.features?.length ?? 0 > 0"
        class="mb-4"
      >
        <div class="mb-1 text-overline font-weight-bold">
          {{ $t('label.whats_new_features') }}
        </div>
        <div
          v-for="(feature, fIndex) in release.features"
          :key="fIndex"
          class="mb-3"
        >
          <div class="d-flex align-start">
            <v-icon
              color="success"
              class="mr-3 flex-shrink-0"
            >
              mdi-new-box
            </v-icon>
            <div>
              <div class="font-weight-medium">
                {{ feature.title }}
              </div>
              <div
                v-if="feature.description"
                class="text-body-2 text--secondary mt-1"
              >
                {{ feature.description }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="release.improvements?.length ?? 0 > 0"
        class="mb-4"
      >
        <div class="mb-1 text-overline font-weight-bold">
          {{ $t('label.whats_new_improvements') }}
        </div>
        <div
          v-for="(improvement, iIndex) in release.improvements"
          :key="iIndex"
          class="mb-3"
        >
          <div class="d-flex align-start">
            <v-icon
              color="success"
              class="mr-3 flex-shrink-0"
            >
              mdi-plus-box
            </v-icon>
            <div>
              <div class="font-weight-medium">
                {{ improvement.title }}
              </div>
              <div
                v-if="improvement.description"
                class="text-body-2 text--secondary mt-1"
              >
                {{ improvement.description }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!showAllUpdates && releaseNotes.length > maxVisibleReleases"
      class="d-flex justify-center mb-2"
    >
      <v-btn
        text
        color="primary"
        @click="showOlderReleases = !showOlderReleases"
      >
        {{
          showOlderReleases
            ? $t('button.show_latest_updates')
            : $t('button.show_older_updates')
        }}
      </v-btn>
    </div>

    <template #actions>
      <v-spacer />
      <v-btn
        color="primary"
        @click="close"
      >
        {{ $t('button.close') }}
      </v-btn>
    </template>
  </new-modal-dialog>
</template>

<script setup lang="ts">
import storage from '@/services/local-storage';
import NewModalDialog from '@/components/dialogs/NewModalDialog.vue';
import { computed, ref } from 'vue';
import { releaseNotes } from './release-notes';
import appVersion from '@/app-version';

const props = withDefaults(
  defineProps<{
    showAllUpdates?: boolean;
  }>(),
  {
    showAllUpdates: false,
  },
);

const emit = defineEmits(['close']);

const dialog = ref(false);

const maxVisibleReleases = 2;
const showOlderReleases = ref(false);

const isShowingOlderReleases = computed(() => {
  if (props.showAllUpdates) return true;
  return showOlderReleases.value;
});

const visibleReleaseNotes = computed(() => {
  if (isShowingOlderReleases.value) return releaseNotes;
  return releaseNotes.slice(0, maxVisibleReleases);
});

const close = () => {
  dialog.value = false;
  storage.setReleaseNotesDisplayed(appVersion);
  emit('close');
};

</script>
